import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Select, SelectItem, Spinner } from "@heroui/react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { sortPeriods } from "@/features/S2Dashboard/model/utils";
import K1FilterBar from "../components/K1FilterBar";
import { useK1DashboardContext } from "../context/K1DashboardProvider";
import { k1Api } from "../services/api";
import { getQuarterFromYYYYMM } from "../utils/quarters";
import { ScrollableX } from "../components/ScrollableX";
import WorldMapChoropleth, {
  type WorldMapItem,
} from "../components/WorldMapChoropleth";
// ─── Constants ───────────────────────────────────────────────────────────────

const PALETTE = [
  "#0f62fe",
  "#6929c4",
  "#005d5d",
  "#9f1853",
  "#fa4d56",
  "#570408",
  "#198038",
  "#002d9c",
  "#ee538b",
  "#b28600",
  "#009d9a",
  "#012749",
  "#8a3800",
  "#a56eff",
  "#004d5b",
];

// ─── Utility ─────────────────────────────────────────────────────────────────

function parseNum(v: unknown): number | null {
  if (v === null || v === undefined || v === "" || v === "—") return null;
  const s = String(v)
    .replace(/,/g, "")
    .replace(/%/g, "")
    .replace(/x$/i, "")
    .trim();
  if (!s || s.toLowerCase() === "nan") return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

function fmtCompact(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1e9) return (v / 1e9).toFixed(1) + "B";
  if (abs >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (abs >= 1e3) return (v / 1e3).toFixed(0) + "K";
  return v.toFixed(0);
}

function fmtValue(v: number, unit: string, currency?: string): string {
  if (unit === "%") return v.toFixed(2) + "%";
  if (unit === "x") return v.toFixed(2) + "x";
  if (unit === "currency") {
    const formatted = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(v);
    return currency ? `${formatted} ${currency}` : formatted;
  }
  return v.toFixed(0);
}

// ─── FundChart ───────────────────────────────────────────────────────────────

function FundChart({
  title,
  labels,
  data,
  color,
  unit,
  currency,
}: {
  title: string;
  labels: string[];
  data: (number | null)[];
  color: string;
  unit: "currency" | "%" | "x" | "";
  currency?: string;
}) {
  const chartData = labels.map((l, i) => ({ label: l, value: data[i] }));
  const gradientId = `grad-${title.replace(/\s/g, "")}`;
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart
          data={chartData}
          margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.15} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} />
          <YAxis
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickFormatter={fmtCompact}
            width={50}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v: unknown) => [
              fmtValue(Number(v ?? 0), unit, currency),
              title,
            ]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={{ r: 3, fill: "#fff", stroke: color, strokeWidth: 2 }}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── InvestmentTimeline ──────────────────────────────────────────────────────

interface TimelineEvent {
  id: string;
  asset: string;
  date: Date;
  type: "Entry" | "Exit";
  color: string;
}

function InvestmentTimeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="text-xs text-gray-400 text-center py-8">
        No timeline data available
      </p>
    );
  }

  const sorted = [...events].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  );
  const PAD = 45 * 86400000;
  const minT = sorted[0].date.getTime() - PAD;
  const maxT = sorted[sorted.length - 1].date.getTime() + PAD;
  const totalMs = maxT - minT || 1;

  // Scale: enough px per event so cards don't stack too much
  const pxPerEvent = 120;
  const contentWidth = Math.max(sorted.length * pxPerEvent, 600);

  const minYear = new Date(minT).getFullYear();
  const maxYear = new Date(maxT).getFullYear();
  const years: number[] = [];
  for (let y = minYear; y <= maxYear; y++) years.push(y);

  const TOTAL_H = 360;
  const BASE_Y = TOTAL_H / 2;
  const STEM_H = 55;
  const CARD_H = 62;

  return (
    <ScrollableX className="pb-2">
      <div
        style={{
          width: contentWidth,
          minWidth: "100%",
          height: TOTAL_H,
          position: "relative",
        }}
      >
        {/* Baseline — centered */}
        <div
          style={{
            position: "absolute",
            top: BASE_Y,
            left: 0,
            right: 0,
            height: 2,
            background: "#e5e7eb",
          }}
        />

        {/* Year ticks */}
        {years.map((y) => {
          const t = new Date(y, 0, 1).getTime();
          const pct = ((t - minT) / totalMs) * 100;
          if (pct < 0 || pct > 100) return null;
          return (
            <div
              key={y}
              style={{
                position: "absolute",
                left: `${pct}%`,
                top: BASE_Y - 10,
                transform: "translateX(-50%)",
              }}
            >
              <div style={{ width: 1, height: 20, background: "#cbd5e1" }} />
              <span
                className="text-[10px] text-gray-400 font-medium"
                style={{
                  position: "absolute",
                  top: 22,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                }}
              >
                {y}
              </span>
            </div>
          );
        })}

        {/* Events */}
        {sorted.map((ev, i) => {
          const pct = ((ev.date.getTime() - minT) / totalMs) * 100;
          const isAbove = i % 2 === 0;
          const dotColor = ev.type === "Entry" ? "#10b981" : "#ef4444";

          const stemTop = isAbove ? BASE_Y - STEM_H : BASE_Y + 2;
          const cardTop = isAbove
            ? BASE_Y - STEM_H - CARD_H - 4
            : BASE_Y + STEM_H + 8;

          return (
            <div
              key={ev.id + i}
              className="group hover:!z-[200]"
              style={{
                position: "absolute",
                left: `${pct}%`,
                top: 0,
                bottom: 0,
                width: 0,
                zIndex: 1,
              }}
            >
              {/* Stem */}
              <div
                style={{
                  position: "absolute",
                  left: -1,
                  top: stemTop,
                  width: 2,
                  height: STEM_H,
                  background: dotColor,
                  opacity: 0.25,
                }}
              />
              {/* Dot */}
              <div
                style={{
                  position: "absolute",
                  left: -7,
                  top: BASE_Y - 7,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: dotColor,
                  border: "2px solid white",
                  zIndex: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                }}
              />
              {/* Card */}
              <div
                className="transition-all duration-150 ease-out group-hover:scale-110 group-hover:shadow-xl"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: cardTop,
                  transform: "translateX(-50%)",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: "6px 10px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  minWidth: 100,
                  textAlign: "center",
                  pointerEvents: "auto",
                }}
              >
                <p className="text-[10px] text-gray-400">
                  {ev.date.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
                <p className="text-xs font-semibold text-gray-700 truncate max-w-[120px]">
                  {ev.asset}
                </p>
                <span
                  className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                    ev.type === "Entry"
                      ? "bg-emerald-50 text-emerald-600"
                      : "bg-red-50 text-red-500"
                  }`}
                >
                  {ev.type}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollableX>
  );
}

// ─── Pie with click-to-reveal tooltip ────────────────────────────────────────

interface PieEntry {
  name: string;
  value: number;
  companies: string[];
}

const RADIAN = Math.PI / 180;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderPieLabel(props: any) {
  const { cx, cy, midAngle, outerRadius, name, percent } = props;
  if (!percent || percent < 0.05) return null;
  const radius = (outerRadius ?? 100) + 22;
  const x = cx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
  const y = cy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {name.length > 18 ? name.slice(0, 16) + "…" : name} (
      {(percent * 100).toFixed(0)}%)
    </text>
  );
}

function PieSection({
  title,
  data,
  paletteOffset = 0,
}: {
  title: string;
  data: PieEntry[];
  paletteOffset?: number;
}) {
  const [activeSlice, setActiveSlice] = useState<PieEntry | null>(null);

  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
        <p className="text-xs text-gray-400 text-center py-8">No data</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
      <div className="flex items-start gap-4">
        <div className="flex-1 [&_svg]:outline-none [&_svg:focus]:outline-none [&_path:focus]:outline-none">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart style={{ outline: "none" }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={2}
                dataKey="value"
                label={renderPieLabel}
                labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
                onClick={(_, idx) => setActiveSlice(data[idx])}
                cursor="pointer"
                isAnimationActive={false}
              >
                {data.map((_, i) => (
                  <Cell
                    key={i}
                    fill={PALETTE[(i + paletteOffset) % PALETTE.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: unknown) => [`${v} companies`]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Click detail panel */}
        <div className="w-48 shrink-0">
          {activeSlice ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-bold text-gray-700 mb-2 border-b border-gray-200 pb-1">
                {activeSlice.name}
              </p>
              <p className="text-[10px] text-gray-400 mb-1">
                {activeSlice.value} companies
              </p>
              <ul className="space-y-0.5 max-h-52 overflow-y-auto">
                {activeSlice.companies.map((c, i) => (
                  <li
                    key={i}
                    className="text-[11px] text-gray-600 truncate"
                    title={c}
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 mt-4">
              Click a slice to see companies
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NavMultiChart ───────────────────────────────────────────────────────────

const NAV_SERIES = [
  { key: "NAVAfterCarryK1",   label: "K1 after carry",   color: "#005d5d", source: "k1",   carry: "after"  },
  { key: "NAVBeforeCarryK1",  label: "K1 before carry",  color: "#0f62fe", source: "k1",   carry: "before" },
  { key: "NAVAfterCarryFund", label: "FUND after carry",  color: "#198038", source: "fund", carry: "after"  },
  { key: "NAVBeforeCarryFund",label: "FUND before carry", color: "#6929c4", source: "fund", carry: "before" },
] as const;

function NavMultiChart({
  labels,
  series,
  currency,
}: {
  labels: string[];
  series: Record<string, (number | null)[]>;
  currency: string;
}) {
  const [sources, setSources] = useState<Set<string>>(new Set(["k1", "fund"]));
  const [carries, setCarries] = useState<Set<string>>(new Set(["after", "before"]));

  const toggle = (set: Set<string>, val: string): Set<string> => {
    const s = new Set(set);
    s.has(val) ? s.delete(val) : s.add(val);
    return s.size === 0 ? set : s; // prevent empty selection
  };

  const visible = NAV_SERIES.filter(
    (s) => sources.has(s.source) && carries.has(s.carry),
  );

  const chartData = labels.map((label, i) => {
    const obj: Record<string, unknown> = { label };
    visible.forEach((s) => {
      obj[s.label] = series[s.key]?.[i] ?? null;
    });
    return obj;
  });

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm font-semibold text-gray-700">Net Asset Value (SI)</p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            {(["k1", "fund"] as const).map((src) => (
              <button
                key={src}
                onClick={() => setSources((prev) => toggle(prev, src))}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                  sources.has(src)
                    ? "bg-slate-700 text-white border-slate-700"
                    : "bg-white text-gray-400 border-gray-200"
                }`}
              >
                {src.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {(["before", "after"] as const).map((carry) => (
              <button
                key={carry}
                onClick={() => setCarries((prev) => toggle(prev, carry))}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                  carries.has(carry)
                    ? "bg-slate-700 text-white border-slate-700"
                    : "bg-white text-gray-400 border-gray-200"
                }`}
              >
                {carry === "before" ? "Before carry" : "After carry"}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        {visible.map((s) => (
          <span key={s.key} className="flex items-center gap-1 text-[11px] text-gray-500">
            <span className="inline-block w-3 h-0.5 rounded-full" style={{ background: s.color, height: 2 }} />
            {s.label}
          </span>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} dy={8} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={fmtCompact} width={50} axisLine={false} tickLine={false} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const sorted = ([...payload] as { name: string; value: number; color: string }[])
                .filter((p) => p.value != null)
                .sort((a, b) => b.value - a.value);
              return (
                <div className="rounded-xl border border-gray-100 bg-white/95 p-3 shadow-lg backdrop-blur-md" style={{ minWidth: 220 }}>
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
                  <div className="flex flex-col gap-1">
                    {sorted.map((entry, i) => (
                      <div key={i} className="flex items-center justify-between gap-4 text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="h-2 w-2 shrink-0 rounded-full" style={{ background: entry.color }} />
                          <span className="text-gray-600">{entry.name}</span>
                        </div>
                        <span className="font-semibold text-gray-800">{fmtValue(entry.value, "currency", currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }}
            cursor={{ stroke: "#94a3b8", strokeWidth: 1, strokeDasharray: "4 4", strokeOpacity: 0.5 }}
          />
          {visible.map((s) => (
            <Line key={s.key} type="monotone" dataKey={s.label} stroke={s.color} strokeWidth={2}
              dot={{ r: 3, fill: "#fff", stroke: s.color, strokeWidth: 2 }}
              activeDot={{ r: 5, fill: s.color, stroke: "#fff", strokeWidth: 2 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── SpaghettiChart ──────────────────────────────────────────────────────────

interface SpaghettiDataset {
  name: string;
  color: string;
  data: (number | null)[];
  hidden: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SpaghettiTooltip({ active, payload, label, unit, currency }: any) {
  if (!active || !payload?.length) return null;
  const sorted = ([...payload] as { name: string; value: number; color: string }[])
    .filter((p) => p.value != null)
    .sort((a, b) => b.value - a.value);
  return (
    <div className="rounded-xl border border-gray-100 bg-white/95 p-3 shadow-lg backdrop-blur-md max-h-64 overflow-y-auto" style={{ minWidth: 200 }}>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <div className="flex flex-col gap-1">
        {sorted.map((entry, i) => (
          <div key={i} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="h-2 w-2 shrink-0 rounded-full" style={{ background: entry.color }} />
              <span className="truncate text-gray-600 max-w-[140px]" title={entry.name}>{entry.name}</span>
            </div>
            <span className="font-semibold text-gray-800 shrink-0">
              {fmtValue(entry.value, unit, currency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpaghettiChart({
  title,
  labels,
  datasets,
  unit,
  currency,
}: {
  title: string;
  labels: string[];
  datasets: SpaghettiDataset[];
  unit: "currency" | "%" | "x" | "";
  currency?: string;
}) {
  const chartData = labels.map((label, i) => {
    const obj: Record<string, unknown> = { label };
    datasets.forEach((ds) => {
      obj[ds.name] = ds.hidden ? null : ds.data[i];
    });
    return obj;
  });
  const visible = datasets.filter((ds) => !ds.hidden);

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <p className="text-sm font-semibold text-gray-700 mb-4">{title}</p>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} dy={8} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={fmtCompact} width={44} />
          <Tooltip
            content={<SpaghettiTooltip unit={unit} currency={currency} />}
            cursor={{ stroke: "#94a3b8", strokeWidth: 1, strokeDasharray: "4 4", strokeOpacity: 0.5 }}
          />
          {visible.map((ds) => (
            <Line key={ds.name} type="monotone" dataKey={ds.name} stroke={ds.color} strokeWidth={2}
              dot={{ r: 3, fill: "#fff", stroke: ds.color, strokeWidth: 1.5 }}
              activeDot={{ r: 5, fill: ds.color, stroke: "#fff", strokeWidth: 2 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Main View ───────────────────────────────────────────────────────────────

export default function K1AnalyticsView() {
  const { selectedFundIds, funds, availablePeriods } = useK1DashboardContext();

  const activeFundId = selectedFundIds[0] ?? null;
  const activeFund = funds.find((f) => (f.id ?? f.fund_id) === activeFundId);
  const currency = activeFund?.fund_currency ?? "";

  // Period selector for pie charts — default to most recent
  const sortedPeriods = useMemo(
    () => sortPeriods(availablePeriods).reverse(),
    [availablePeriods],
  );
  const [piePeriod, setPiePeriod] = useState<string | null>(null);
  // Auto-set to most recent when periods load
  useEffect(() => {
    if (sortedPeriods.length > 0 && !piePeriod) {
      setPiePeriod(sortedPeriods[0]);
    }
  }, [sortedPeriods]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch capital account (all periods) ────────────────────────────────
  const { data: caData, isLoading: caLoading } = useQuery({
    queryKey: ["k1", "capital-account-all", activeFundId],
    queryFn: () => k1Api.getCapitalAccount(activeFundId!, null, true),
    enabled: !!activeFundId,
    staleTime: 60_000,
  });

  const { data: posData } = useQuery({
    queryKey: ["k1", "positions-all", activeFundId],
    queryFn: () =>
      k1Api.getPositions({ selection: { mode: "ids", ids: [activeFundId!] } }),
    enabled: !!activeFundId,
    staleTime: 60_000,
  });

  // Positions for the active fund — already scoped server-side
  const fundPositionsForIssuers = useMemo(
    () => (posData?.data ?? []) as Record<string, unknown>[],
    [posData],
  );

  const issuerIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of fundPositionsForIssuers) {
      const id = Number(row.issuer_id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return Array.from(ids).sort((a, b) => a - b);
  }, [fundPositionsForIssuers]);

  const {
    data: issData,
    isLoading: issuersLoading,
    isError: issuersError,
  } = useQuery({
    queryKey: ["k1", "issuers-by-ids", issuerIds],
    queryFn: () => k1Api.getIssuers({ ids: issuerIds, slim: true }),
    staleTime: 60_000,
    enabled: issuerIds.length > 0,
  });

  const issuerMap = useMemo(() => {
    const map = new Map<number, Record<string, unknown>>();
    for (const iss of issData?.data ?? []) {
      map.set(Number(iss.id ?? iss.issuer_id), iss as Record<string, unknown>);
    }
    return map;
  }, [issData]);

  // ── Capital account series ─────────────────────────────────────────────
  const { labels, series } = useMemo(() => {
    const rows = (caData?.data ?? []) as Record<string, unknown>[];
    const sorted = [...rows].sort(
      (a, b) => Number(a.reporting_date ?? 0) - Number(b.reporting_date ?? 0),
    );
    const labels = sorted.map((r) => getQuarterFromYYYYMM(r.reporting_date));
    return {
      labels,
      series: {
        Committed: sorted.map((r) => parseNum(r.si_total_commitment_k1)),
        Unfunded: sorted.map((r) => parseNum(r.si_unfunded_commitment_k1)),
        NAVAfterCarryK1: sorted.map((r) => parseNum(r.si_nav_after_carry_k1)),
        NAVBeforeCarryK1: sorted.map((r) => parseNum(r.si_nav_before_carry_k1)),
        NAVAfterCarryFund: sorted.map((r) => parseNum(r.si_nav_after_carry_tf)),
        NAVBeforeCarryFund: sorted.map((r) => parseNum(r.si_nav_before_carry_tf)),
        Distributions: sorted.map((r) => parseNum(r.si_distributions_k1)),
        IRR: sorted.map((r) => {
          const v = parseNum(r.si_net_irr_k1);
          return v !== null ? v * 100 : null;
        }),
        TVPI: sorted.map((r) => parseNum(r.si_tvpi_k1)),
      },
    };
  }, [caData]);

  const hasMultiplePeriods = labels.length > 1;

  // ── Positions for this fund ────────────────────────────────────────────
  const fundPositions = useMemo(() => {
    if (!activeFundId || !posData) return [];
    return ((posData.data ?? []) as Record<string, unknown>[]).filter(
      (r) => Number(r.fund_id) === activeFundId,
    );
  }, [posData, activeFundId]);

  // ── Timeline events — one entry/exit per issuer from the most recent position
  const timelineEvents = useMemo(() => {
    // Keep only the most recent position per issuer (highest reporting_date)
    const latestByIssuer = new Map<number, Record<string, unknown>>();
    for (const pos of fundPositions) {
      const issuerId = Number(pos.issuer_id);
      const existing = latestByIssuer.get(issuerId);
      if (
        !existing ||
        Number(pos.reporting_date) > Number(existing.reporting_date)
      ) {
        latestByIssuer.set(issuerId, pos);
      }
    }

    const events: TimelineEvent[] = [];
    for (const [issuerId, pos] of latestByIssuer) {
      const iss = issuerMap.get(issuerId);
      const name = String(
        iss?.company_name_long ?? iss?.brand_name ?? `Issuer ${issuerId}`,
      );
      const color = PALETTE[issuerId % PALETTE.length];

      const entryRaw = pos.first_investment_date as string | null;
      if (entryRaw) {
        const d = new Date(entryRaw);
        if (!isNaN(d.getTime()))
          events.push({ id: `entry-${issuerId}`, asset: name, date: d, type: "Entry", color });
      }

      const exitRaw = pos.exit_date as string | null;
      if (exitRaw) {
        const d = new Date(exitRaw);
        if (!isNaN(d.getTime()))
          events.push({ id: `exit-${issuerId}`, asset: name, date: d, type: "Exit", color });
      }
    }
    return events;
  }, [fundPositions, issuerMap]);

  // ── Pie data — filtered by piePeriod ───────────────────────────────────
  const { countryPie, sectorPie } = useMemo(() => {
    const countryMap = new Map<
      string,
      { count: number; companies: string[] }
    >();
    const sectorMap = new Map<string, { count: number; companies: string[] }>();
    const seenIssuers = new Set<number>();

    // Filter positions by selected pie period (fall back to all if field missing)
    const positions = piePeriod
      ? (() => {
          const filtered = fundPositions.filter(
            (p) => getQuarterFromYYYYMM(p.reporting_date) === piePeriod,
          );
          return filtered.length > 0 ? filtered : fundPositions;
        })()
      : fundPositions;

    for (const pos of positions) {
      const issuerId = Number(pos.issuer_id);
      if (seenIssuers.has(issuerId)) continue;
      seenIssuers.add(issuerId);
      const iss = issuerMap.get(issuerId);
      if (!iss) continue;
      const companyName = String(
        iss.company_name_long ?? iss.brand_name ?? "Unknown",
      );

      const country = String(iss.country_of_domicile ?? "Unknown");
      const cEntry = countryMap.get(country) ?? { count: 0, companies: [] };
      cEntry.count++;
      cEntry.companies.push(companyName);
      countryMap.set(country, cEntry);

      const sector = String(iss.sector ?? "Unknown");
      const sEntry = sectorMap.get(sector) ?? { count: 0, companies: [] };
      sEntry.count++;
      sEntry.companies.push(companyName);
      sectorMap.set(sector, sEntry);
    }

    const toArr = (
      m: Map<string, { count: number; companies: string[] }>,
    ): PieEntry[] =>
      [...m.entries()]
        .sort((a, b) => b[1].count - a[1].count)
        .map(([name, { count, companies }]) => ({
          name,
          value: count,
          companies,
        }));

    return { countryPie: toArr(countryMap), sectorPie: toArr(sectorMap) };
  }, [fundPositions, issuerMap, piePeriod]);

  // ── Portfolio companies map data — filtered by piePeriod, same as the pie charts ──
  const portfolioMapItems = useMemo<WorldMapItem[]>(() => {
    // Unlike the pie charts below, this map has an explicit "Period"
    // selector next to it — silently falling back to all periods when the
    // selected one has no positions would show data the user didn't ask
    // for while the selector still displays their choice. Empty is honest.
    const positions = piePeriod
      ? fundPositions.filter(
          (p) => getQuarterFromYYYYMM(p.reporting_date) === piePeriod,
        )
      : fundPositions;

    const seenIssuers = new Set<number>();
    const items: WorldMapItem[] = [];
    for (const pos of positions) {
      const issuerId = Number(pos.issuer_id);
      if (seenIssuers.has(issuerId)) continue;
      seenIssuers.add(issuerId);
      const iss = issuerMap.get(issuerId);
      if (!iss) continue;
      items.push({
        name: String(iss.company_name_long ?? iss.brand_name ?? "Unknown"),
        countryIsoNumeric: (iss.country_iso_numeric as string) ?? null,
      });
    }
    return items;
  }, [fundPositions, issuerMap, piePeriod]);

  // ── Asset-level spaghetti charts ───────────────────────────────────────
  const [hiddenAssets, setHiddenAssets] = useState<Set<number>>(new Set());

  const assetMap = useMemo(() => {
    const map = new Map<number, { name: string; color: string }>();
    let idx = 0;
    for (const pos of fundPositions) {
      const issuerId = Number(pos.issuer_id);
      if (Number.isFinite(issuerId) && issuerId > 0 && !map.has(issuerId)) {
        const iss = issuerMap.get(issuerId);
        const name = String(
          iss?.company_name_long ?? iss?.brand_name ?? `Issuer ${issuerId}`,
        );
        map.set(issuerId, { name, color: PALETTE[idx++ % PALETTE.length] });
      }
    }
    return map;
  }, [fundPositions, issuerMap]);

  const posLabels = useMemo(() => {
    const seen = new Map<string, number>();
    for (const pos of fundPositions) {
      const rd = Number(pos.reporting_date);
      const q = getQuarterFromYYYYMM(pos.reporting_date);
      if (q && (!seen.has(q) || rd < seen.get(q)!)) seen.set(q, rd);
    }
    return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([q]) => q);
  }, [fundPositions]);

  const positionByIssuerAndQuarter = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    for (const pos of fundPositions) {
      const issuerId = Number(pos.issuer_id);
      const quarter = getQuarterFromYYYYMM(pos.reporting_date);
      if (!Number.isFinite(issuerId) || !quarter) continue;
      map.set(`${issuerId}:${quarter}`, pos);
    }
    return map;
  }, [fundPositions]);

  const { datasetsFMV, datasetsIRR, datasetsMOIC } = useMemo(() => {
    const fmv: SpaghettiDataset[] = [];
    const irr: SpaghettiDataset[] = [];
    const moic: SpaghettiDataset[] = [];
    for (const [issuerId, info] of assetMap) {
      const hidden = hiddenAssets.has(issuerId);
      const fmvD = posLabels.map((label) => {
        const pos = positionByIssuerAndQuarter.get(`${issuerId}:${label}`);
        return pos ? parseNum(pos.fair_value) : null;
      });
      const irrD = posLabels.map((label) => {
        const pos = positionByIssuerAndQuarter.get(`${issuerId}:${label}`);
        const v = pos ? parseNum(pos.irr) : null;
        return v !== null ? v * 100 : null;
      });
      const moicD = posLabels.map((label) => {
        const pos = positionByIssuerAndQuarter.get(`${issuerId}:${label}`);
        return pos ? parseNum(pos.moic) : null;
      });
      fmv.push({ name: info.name, color: info.color, data: fmvD, hidden });
      irr.push({ name: info.name, color: info.color, data: irrD, hidden });
      moic.push({ name: info.name, color: info.color, data: moicD, hidden });
    }
    return { datasetsFMV: fmv, datasetsIRR: irr, datasetsMOIC: moic };
  }, [assetMap, posLabels, positionByIssuerAndQuarter, hiddenAssets]);

  // ── Render ─────────────────────────────────────────────────────────────

  if (selectedFundIds.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <K1FilterBar singleSelect hidePeriod />
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      </div>
    );
  }

  if (caLoading) {
    return (
      <div className="flex flex-col gap-4">
        <K1FilterBar singleSelect hidePeriod />
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading analytics…" color="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 bg-white">
      <K1FilterBar singleSelect hidePeriod />

      {/* ── Timeline (full width, top) ────────────────────────────────── */}
      <section>
        <h2 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
          Investment Timeline
        </h2>
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <InvestmentTimeline events={timelineEvents} />
        </div>
      </section>

      {/* ── Charts (only if multiple periods) ─────────────────────────── */}
      {hasMultiplePeriods ? (
        <>
          <section>
            <h2 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
              Capital Dynamics
            </h2>
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-5">
              <FundChart
                title="Total Commitment"
                labels={labels}
                data={series.Committed}
                color="#0f62fe"
                unit="currency"
                currency={currency}
              />
              <FundChart
                title="Unfunded Commitment"
                labels={labels}
                data={series.Unfunded}
                color="#6929c4"
                unit="currency"
                currency={currency}
              />
              <NavMultiChart labels={labels} series={series} currency={currency} />
              <FundChart
                title="Distributions"
                labels={labels}
                data={series.Distributions}
                color="#198038"
                unit="currency"
                currency={currency}
              />
            </div>
          </section>
          <section>
            <h2 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
              Performance
            </h2>
            <div className="grid grid-cols-2 gap-5">
              <FundChart
                title="Net IRR"
                labels={labels}
                data={series.IRR}
                color="#9f1853"
                unit="%"
              />
              <FundChart
                title="TVPI"
                labels={labels}
                data={series.TVPI}
                color="#002d9c"
                unit="x"
              />
            </div>
          </section>
        </>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-400">
          Only one reporting period available — charts require at least two
          periods.
        </div>
      )}

      {/* ── Assets Details ────────────────────────────────────────────── */}
      {posLabels.length > 0 && assetMap.size > 0 && (
        <section>
          <h2 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
            Assets Details
          </h2>
          <div className="flex flex-col xl:flex-row gap-5">
            <div className="flex flex-1 flex-col gap-5">
              <SpaghettiChart
                title={`Fair Value${currency ? " (" + currency + ")" : ""}`}
                labels={posLabels}
                datasets={datasetsFMV}
                unit="currency"
                currency={currency}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <SpaghettiChart
                  title="IRR (%)"
                  labels={posLabels}
                  datasets={datasetsIRR}
                  unit="%"
                />
                <SpaghettiChart
                  title="MOIC (x)"
                  labels={posLabels}
                  datasets={datasetsMOIC}
                  unit="x"
                />
              </div>
            </div>
            <div className="w-full xl:w-56 shrink-0 rounded-xl border border-gray-100 bg-white overflow-hidden flex flex-col shadow-sm">
              <div className="border-b border-gray-100 bg-gray-50/50 p-4">
                <h3 className="text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">
                  Compare Assets
                </h3>
                <p className="text-[11px] text-gray-500 mb-3">
                  Toggle companies visibility
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="flat"
                    className="flex-1 text-[11px] bg-white"
                    onPress={() => setHiddenAssets(new Set())}
                  >
                    Show All
                  </Button>
                  <Button
                    size="sm"
                    variant="flat"
                    className="flex-1 text-[11px] bg-white"
                    onPress={() => setHiddenAssets(new Set(assetMap.keys()))}
                  >
                    Hide All
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[400px] xl:max-h-none">
                {[...assetMap.entries()].map(([id, info]) => (
                  <div
                    key={id}
                    onClick={() =>
                      setHiddenAssets((prev) => {
                        const s = new Set(prev);
                        s.has(id) ? s.delete(id) : s.add(id);
                        return s;
                      })
                    }
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 transition-all"
                    style={{ opacity: hiddenAssets.has(id) ? 0.4 : 1 }}
                  >
                    <div
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ background: info.color }}
                    />
                    <span
                      className="truncate text-xs font-semibold text-gray-700"
                      title={info.name}
                    >
                      {info.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Pie Charts with period selector ───────────────────────────── */}
      <section>
        <div className="mb-4 flex items-center gap-4">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
            Portfolio Breakdown
          </h2>
          {sortedPeriods.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                Period
              </span>
              <Select
                size="sm"
                selectedKeys={piePeriod ? new Set([piePeriod]) : new Set()}
                onSelectionChange={(keys) => {
                  if (keys === "all") return;
                  const val = Array.from(keys)[0] as string;
                  if (val) setPiePeriod(val);
                }}
                classNames={{
                  trigger: "h-6 min-h-6 bg-gray-50 shadow-none text-xs w-32",
                  value: "text-xs",
                }}
                aria-label="Period for breakdown"
              >
                {sortedPeriods.map((p) => (
                  <SelectItem key={p}>{p}</SelectItem>
                ))}
              </Select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <PieSection
            title="Portfolio by Country"
            data={countryPie}
            paletteOffset={0}
          />
          <PieSection
            title="Portfolio by Sector"
            data={sectorPie}
            paletteOffset={5}
          />
        </div>
      </section>

      {/* ── Portfolio Companies Map ─────────────────────────────────────── */}
      <WorldMapChoropleth
        title="Portfolio Companies Map"
        items={portfolioMapItems}
        unitSingular="company"
        unitPlural="companies"
        isLoading={issuersLoading}
        isError={issuersError}
        headerExtra={
          sortedPeriods.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                Period
              </span>
              <Select
                size="sm"
                selectedKeys={piePeriod ? new Set([piePeriod]) : new Set()}
                onSelectionChange={(keys) => {
                  if (keys === "all") return;
                  const val = Array.from(keys)[0] as string;
                  if (val) setPiePeriod(val);
                }}
                classNames={{
                  trigger: "h-6 min-h-6 bg-gray-50 shadow-none text-xs w-32",
                  value: "text-xs",
                }}
                aria-label="Period for portfolio companies map"
              >
                {sortedPeriods.map((p) => (
                  <SelectItem key={p}>{p}</SelectItem>
                ))}
              </Select>
            </div>
          )
        }
      />
    </div>
  );
}
